import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const IMG = process.env.REACT_APP_ATTACHMENT_URL;

// Render a candidate image through the asset CDN (handles raw S3 urls, the CDN
// host, and bare keys — same approach as scanAssetUrl).
const candidateImg = (url, qs = '?w=320&h=320&f=inside&q=85') => {
  const noQuery = String(url || '').split('?')[0];
  const aws = noQuery.split('.amazonaws.com/');
  const key = aws.length === 2 ? aws[1] : (IMG ? (noQuery.split(`${IMG}/`)[1] || noQuery) : noQuery);
  return IMG ? `${IMG}/${key}${qs}` : url;
};

// Review queue of candidate images for image-less wines (harvested from matched
// bottle scans, and later ecom/winery/external sources). Approve attaches the
// image to the wine; reject suppresses it.
export default function ImageCandidates() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [types, setTypes] = useState({}); // candidateId -> BOTTLE|LABEL

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await client.service('admin/libation-image-candidate').find({
        query: { status: 'pending', $limit: 50, $sort: { score: -1 } },
      });
      setGroups(res.data || []);
      const t = {};
      (res.data || []).forEach((c) => { t[c.id] = c.proposedType || 'BOTTLE'; });
      setTypes(t);
    } catch (e) {
      setError('Failed to load image candidates.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (c, patch) => {
    setBusyId(c.id);
    try {
      await client.service('admin/libation-image-candidate').patch(c.id, patch);
      setGroups((cur) => (cur || []).filter((g) => g.id !== c.id));
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const approve = (c) => resolve(c, { status: 'approved', type: types[c.id] });
  const reject = (c) => resolve(c, { status: 'rejected' });

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Image Candidates' },
        ]}
        />
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-1">Image Candidates</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Proposed images for wines that have none — gathered from confidently-matched
          bottle scans (more sources later). <strong>Approve</strong> to attach the image
          to the wine, or <strong>reject</strong> to skip it (won&apos;t be proposed again).
          Approved images land on the wine (all vintages); you can re-level to a vintage
          afterward on the wine&apos;s page.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && groups === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && groups && groups.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          No image candidates to review. The nightly harvest will add new ones here.
        </div>
      )}

      {!error && groups && groups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groups.map((c) => (
            <div key={c.id} className="bg-white shadow rounded-lg overflow-hidden flex flex-col">
              <a href={candidateImg(c.imageUrl, '?w=1200&h=1200&f=inside&q=95')} target="_blank" rel="noopener noreferrer">
                <img
                  src={candidateImg(c.imageUrl)}
                  alt=""
                  className="w-full h-56 object-contain bg-gray-50"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
              </a>
              <div className="p-3 flex-1 flex flex-col">
                <div className="text-sm font-semibold text-gray-900 truncate">{c.libationName || 'wine'}</div>
                <div className="mt-0.5 text-xs text-gray-500">
                  {c.listingId ? (
                    <NavLink to={`/admin/listings/${c.listingId}`} className="text-indigo-700 hover:underline">
                      {c.listingName || 'listing'}
                    </NavLink>
                  ) : <span className="text-amber-600">no listing</span>}
                  <span className="text-gray-400"> · {c.source}{typeof c.score === 'number' ? ` · match ${(c.score * 100).toFixed(0)}%` : ''}</span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <select
                    value={types[c.id] || 'BOTTLE'}
                    onChange={(e) => setTypes((t) => ({ ...t, [c.id]: e.target.value }))}
                    className="text-xs border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                    title="Attach as a bottle photo or a label image"
                  >
                    <option value="BOTTLE">Bottle</option>
                    <option value="LABEL">Label</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => reject(c)}
                    disabled={busyId === c.id}
                    className="ml-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => approve(c)}
                    disabled={busyId === c.id}
                    className="px-4 py-1.5 text-sm border border-transparent rounded-md text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                  >
                    {busyId === c.id ? 'Working…' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
